import type { SequenceRead } from './sequenceHead.ts'
import { bestPrefixEditDistance, reverseComplement, toMasks } from './iupac.ts'
import { stripAdapter } from './primerRegistry.ts'

export type PrimerTrimVerdict = 'TRIMMED' | 'UNTRIMMED' | 'UNKNOWN'

export type PrimerPair = { forward: string; reverse: string }

export type PrimerTrimOptions = {
  /** Bases a primer may start after the read start: heterogeneity spacers, inline tags, leftover index bases. */
  maxOffset: number
  /**
   * Only the primer's 3′-most bases are matched. Customer primers often carry a 5′ adapter or tag
   * that the sequencer never reads back, so matching the full string would miss every read.
   */
  coreLength: number
  /** Edit distance allowed per core base. */
  maxErrorRate: number
  minReads: number
  /** At or below this share of primer-bearing reads, the file counts as trimmed. */
  trimmedAtMost: number
  /** At or above this share, the primers are still on. Between the two thresholds: `UNKNOWN`. */
  untrimmedAtLeast: number
  /**
   * `read`: a read bears a primer when it starts with either primer or ends with the reverse complement
   * of either — so single-end and merged reads in both orientations count fully. `start`: the 5′ start
   * only, which caps a 50/50-oriented Nanopore file at about half its reads.
   */
  evidence: 'read' | 'start'
}

export const DEFAULT_PRIMER_TRIM_OPTIONS: PrimerTrimOptions = {
  /* inline sample tags on multiplexed uploads reach 12 bases before any spacer */
  maxOffset: 16,
  coreLength: 20,
  maxErrorRate: 0.15,
  minReads: 20,
  trimmedAtMost: 0.1,
  untrimmedAtLeast: 0.5,
  evidence: 'read'
}

export type PrimerTrimResult = PrimerStartRates & {
  verdict: PrimerTrimVerdict
  reason: string
}

type PreparedPrimer = { masks: number[]; maxStart: number; maxErrors: number }

const prepare = (primer: string, options: PrimerTrimOptions): PreparedPrimer | null => {
  const clean = stripAdapter(primer)
  if (clean.length < 8) {
    return null
  }
  const core = clean.slice(-Math.min(options.coreLength, clean.length))
  return {
    masks: toMasks(core),
    maxStart: options.maxOffset + (clean.length - core.length),
    maxErrors: Math.floor(core.length * options.maxErrorRate)
  }
}

/** Edits per core base of the best-matching primer at the read start, or `null` when none matches. */
const matchAtStart = (read: string, primers: PreparedPrimer[]): number | null => {
  let best: number | null = null
  for (const primer of primers) {
    const distance = bestPrefixEditDistance(primer.masks, read, primer.maxStart, primer.maxErrors)
    if (distance <= primer.maxErrors) {
      const perBase = distance / primer.masks.length
      best = best === null ? perBase : Math.min(best, perBase)
    }
  }
  return best
}

export type PrimerStartRates = {
  /** Share of primer-bearing reads, by the `evidence` rule. The number the verdict is taken on. */
  primerFoundRate: number
  /** Share with either primer at the 5′ start. */
  startRate: number
  /** Share with the forward primer at the 5′ start: R1, or a merged/single read in forward orientation. */
  forwardRate: number
  /** Share with the reverse primer at the 5′ start: R2, or a merged/single read in reverse orientation. */
  reverseRate: number
  /** Share ending in the reverse complement of either primer. */
  primerAtEndRate: number
  /** Share bearing the pair's rarer primer, by the `evidence` rule: near zero when the reads carry only one of them. */
  weakerPrimerRate: number
  /**
   * Mean edits per core base over the matches. Tells a pair apart from a lookalike that scores the same
   * rate — primers in conserved rRNA stretches (12S MarVer1 vs 16S V4) match each other's sites loosely.
   */
  matchErrorRate: number
  readsExamined: number
}

const NO_READS: PrimerStartRates = {
  primerFoundRate: 0,
  startRate: 0,
  forwardRate: 0,
  reverseRate: 0,
  primerAtEndRate: 0,
  weakerPrimerRate: 0,
  matchErrorRate: 0,
  readsExamined: 0
}

export const primerStartRates = (
  reads: readonly SequenceRead[],
  primers: PrimerPair,
  options: PrimerTrimOptions = DEFAULT_PRIMER_TRIM_OPTIONS
): PrimerStartRates | null => {
  const forward = prepare(primers.forward, options)
  const reverse = prepare(primers.reverse, options)
  const prepared = [forward, reverse].flatMap((p) => (p ? [p] : []))
  if (prepared.length === 0) {
    return null
  }
  const usable = reads.filter((read) => read.sequence.length >= 30)
  if (usable.length === 0) {
    return NO_READS
  }
  const window = Math.max(...prepared.map((p) => p.maxStart + 2 * p.masks.length))
  let bearing = 0
  let atStart = 0
  let forwardAtStart = 0
  let reverseAtStart = 0
  let atEnd = 0
  let forwardBearing = 0
  let reverseBearing = 0
  let errors = 0
  let matches = 0
  for (const read of usable) {
    const fe = forward ? matchAtStart(read.sequence, [forward]) : null
    const re = reverse ? matchAtStart(read.sequence, [reverse]) : null
    const tail = reverseComplement(read.sequence.slice(-window))
    const fee = forward ? matchAtStart(tail, [forward]) : null
    const ree = reverse ? matchAtStart(tail, [reverse]) : null
    const ee = fee === null ? ree : ree === null ? fee : Math.min(fee, ree)
    const f = fe !== null
    const r = re !== null
    const end = ee !== null
    const byEnd = options.evidence === 'read'
    if (f || (byEnd && fee !== null)) {
      forwardBearing++
    }
    if (r || (byEnd && ree !== null)) {
      reverseBearing++
    }
    for (const e of [fe, re, ee]) {
      if (e !== null) {
        errors += e
        matches++
      }
    }
    if (f) {
      forwardAtStart++
    }
    if (r) {
      reverseAtStart++
    }
    if (f || r) {
      atStart++
    }
    if (end) {
      atEnd++
    }
    if (f || r || (end && options.evidence === 'read')) {
      bearing++
    }
  }
  const n = usable.length
  return {
    primerFoundRate: bearing / n,
    startRate: atStart / n,
    forwardRate: forwardAtStart / n,
    reverseRate: reverseAtStart / n,
    primerAtEndRate: atEnd / n,
    weakerPrimerRate: (forward && reverse ? Math.min(forwardBearing, reverseBearing) : 0) / n,
    matchErrorRate: matches === 0 ? 0 : errors / matches,
    readsExamined: n
  }
}

/** Whether one file's reads still carry the primers of one pair, judged per read. */
export const detectPrimerTrimming = (
  reads: readonly SequenceRead[],
  primers: PrimerPair,
  options: PrimerTrimOptions = DEFAULT_PRIMER_TRIM_OPTIONS
): PrimerTrimResult => {
  const rates = primerStartRates(reads, primers, options)
  if (!rates) {
    return { verdict: 'UNKNOWN', ...NO_READS, reason: 'no usable primer sequence' }
  }
  if (rates.readsExamined < options.minReads) {
    return { verdict: 'UNKNOWN', ...rates, reason: `only ${rates.readsExamined} reads to judge` }
  }
  if (rates.primerFoundRate <= options.trimmedAtMost) {
    return { verdict: 'TRIMMED', ...rates, reason: 'reads carry no primer at either end' }
  }
  if (rates.primerFoundRate >= options.untrimmedAtLeast) {
    return { verdict: 'UNTRIMMED', ...rates, reason: 'reads carry the primers' }
  }
  return { verdict: 'UNKNOWN', ...rates, reason: 'primers found on some reads only' }
}
